import axios from '../utils/axios';

const API_URL = `${process.env.REACT_APP_API_URL}/v1/client/`;

const ENUM_FIELDS = ['status', 'logoPosition', 'progressbarType', 'assignmentScope'];

const normalizeSurveyPayload = (data) => {
    if (!data?.survey) {
        return data;
    }

    const survey = { ...data.survey };
    ENUM_FIELDS.forEach((field) => {
        if (typeof survey[field] === 'string') {
            survey[field] = survey[field].toUpperCase();
        }
    });

    // Appearance position fields stay lowercase strings on the API DTO
    ['headerPosition', 'descriptionPosition'].forEach((field) => {
        if (typeof survey[field] === 'string') {
            survey[field] = survey[field].toLowerCase();
        }
    });

    return {
        ...data,
        survey,
        questions: (data.questions || []).map((question) => ({
            ...question,
            isRequired: question.isRequired ?? question.required ?? false,
        })),
    };
};

const buildSurveyQuery = ({ sortBy, sortOrder, searchField, searchTerm, statusFilter } = {}) => {
    const parts = [];
    if (sortBy) {
        parts.push(`sortBy==${sortBy}`);
    }
    if (sortOrder) {
        parts.push(`sortOrder==${sortOrder}`);
    }
    if (searchTerm?.trim()) {
        const field = searchField || 'title';
        parts.push(`${field}==*${searchTerm.trim()}*`);
    }
    if (statusFilter) {
        parts.push(`status==${statusFilter}`);
    }
    return parts.join(',');
};

const getClinicSurveys = (clinicId, pageNo = 0, pageSize = 10, queryOptions = {}) => {
    const q = buildSurveyQuery(queryOptions);
    return axios.get(`${API_URL}clinics/${clinicId}/surveys`, {
        params: { no: pageNo, size: pageSize, ...(q ? { q } : {}) },
    });
};

const getClinicSurveyById = (clinicId, surveyId) =>
    axios.get(`${API_URL}clinics/${clinicId}/surveys/${surveyId}`);

const getClinicSurveyResultStats = (clinicId, surveyId) =>
    axios.get(`${API_URL}clinics/${clinicId}/surveys/${surveyId}/result-stats`);

const getClinicSurveyQuestionAggregates = (clinicId, surveyId) =>
    axios.get(`${API_URL}clinics/${clinicId}/surveys/${surveyId}/question-aggregates`);

const getClinicSurveyResponses = (clinicId, surveyId, pageNo = 0, pageSize = 20, queryOptions = {}) => {
    const parts = [];
    if (queryOptions.sortBy) {
        parts.push(`sortBy==${queryOptions.sortBy}`);
    }
    if (queryOptions.sortOrder) {
        parts.push(`sortOrder==${queryOptions.sortOrder}`);
    }
    if (queryOptions.searchTerm?.trim()) {
        parts.push(`q==*${queryOptions.searchTerm.trim()}*`);
    }
    if (queryOptions.statusFilter) {
        parts.push(`status==${queryOptions.statusFilter}`);
    }
    if (queryOptions.channelFilter) {
        parts.push(`channel==${queryOptions.channelFilter}`);
    }
    const q = parts.join(',');
    return axios.get(`${API_URL}clinics/${clinicId}/surveys/${surveyId}/responses`, {
        params: { no: pageNo, size: pageSize, ...(q ? { q } : {}) },
    });
};

const getClinicSurveyResponseById = (clinicId, surveyId, responseId) =>
    axios.get(`${API_URL}clinics/${clinicId}/surveys/${surveyId}/responses/${responseId}`);

const createClinicSurvey = (clinicId, data) =>
    axios.post(`${API_URL}clinics/${clinicId}/surveys`, normalizeSurveyPayload(data));

const updateClinicSurvey = (clinicId, surveyId, data) =>
    axios.put(`${API_URL}clinics/${clinicId}/surveys/${surveyId}`, normalizeSurveyPayload(data));

const deleteClinicSurvey = (clinicId, surveyId) =>
    axios.delete(`${API_URL}clinics/${clinicId}/surveys/${surveyId}`);

const getSurveyTemplates = (clinicId, pageNo = 0, pageSize = 20, queryOptions = {}) => {
    const q = buildSurveyQuery(queryOptions);
    return axios.get(`${API_URL}clinics/${clinicId}/survey-templates`, {
        params: { no: pageNo, size: pageSize, ...(q ? { q } : {}) },
    });
};

const getSurveyTemplateById = (clinicId, surveyId) =>
    axios.get(`${API_URL}clinics/${clinicId}/survey-templates/${surveyId}`);

const installSurveyTemplate = (clinicId, surveyTemplateId) =>
    axios.post(`${API_URL}clinics/${clinicId}/surveys/install-from-template`, {
        surveyTemplateId,
    });

/** @deprecated Prefer getClinicSurveys */
const getClinicSurveyList = (clinicId) => getClinicSurveys(clinicId, 0, 50);

const surveyService = {
    getClinicSurveys,
    getClinicSurveyById,
    getClinicSurveyResultStats,
    getClinicSurveyQuestionAggregates,
    getClinicSurveyResponses,
    getClinicSurveyResponseById,
    createClinicSurvey,
    updateClinicSurvey,
    deleteClinicSurvey,
    getSurveyTemplates,
    getSurveyTemplateById,
    installSurveyTemplate,
    getClinicSurveyList,
};

export default surveyService;
